import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SignedPrintAuthorization } from "@/Common/Models/SignedPrintAuthorization";
import { SentFaxUiStatus } from "@/Common/Models/UI/SentFaxUiStatus";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { SentFaxStatusHelper } from "@/Patient/Helpers/SentFaxStatusHelper";
import { SentFaxInfo } from "@/Patient/Models/Fax/SentFaxInfo";
import { ResendFaxButton } from "./SentFaxStatus/ResendFaxButton";
import { SentFaxInfoBox } from "./SentFaxStatus/SentFaxInfoBox";

import styles from "./PlanActionLinks.module.scss";

interface PlanActionLinksProps {
    canSignElement?: boolean;
    onActionClick?: (mode: ExportMode) => void;
    onRevokeSignature: () => void;
    signedPrintAuthorizations: SignedPrintAuthorization[]
    showPortalStatusButton?: boolean;
    portalStatusButton?: JSX.Element;
    encounterId?: string;
    formId?: string;
    shouldDisableSignButton?: boolean;
    sentFaxInfo?: SentFaxInfo[];
    disable?: boolean;
}

export class PlanActionLinks extends BaseReactComponent<PlanActionLinksProps> {
    public render(): JSX.Element {
        const sentFaxInfo = this.props.sentFaxInfo ?? [];
        const isLastSignatureFromCurrentEmployee = SignedPrintAuthorization.isLastSignatureFrom(
            this.props.signedPrintAuthorizations,
            sessionStore.getCurrentEmployee().ContactFacetId,
            this.props.encounterId
        );

        const canQuickSignDocuments = cacheStore.ClinicSettings.EnableQuickDocumentSigning;
        const isSignOnPrintAccessValid = canQuickSignDocuments || sessionStore.signOnPrintAccess;
        const showSign = !isLastSignatureFromCurrentEmployee && isSignOnPrintAccessValid && this.props.canSignElement;
        const showRevokeSignature = isLastSignatureFromCurrentEmployee;
        const showUseSigned = this.props.signedPrintAuthorizations?.any() && !isLastSignatureFromCurrentEmployee;
        const showFax = sessionStore.faxAccess && this.props.canSignElement;
        const showSentFaxes = cacheStore.ClinicSettings.EnableFaxSendingIndicator && sentFaxInfo.length > 0;
        const showGeneralResendButton = showSentFaxes && this.props.sentFaxInfo.any(fax => SentFaxStatusHelper.convertToUiStatus(fax.SentStatus) === SentFaxUiStatus.Failed);
        const disableLinkCssClass = this.props.disable ? styles.disabledLink : null;

        return (
            <div className={styles.planActionLinksContainerFormatted}>
                <div>
                    {cacheStore.ClinicSettings.EnableSignedAuthorization && (
                        <div className={`${styles.largeActionLink}${this.props.disable ? " " + styles.disabledActionLink : null}`}>
                            {showSign && (
                                <div>
                                    {this.props.canSignElement && (
                                        <a onClick={() => this.props.onActionClick(ExportMode.Sign)} className={disableLinkCssClass} data-cy="planActionLinks-sign">
                                            {cacheStore.GetString("SignLabel")}
                                        </a>
                                    )}
                                </div>
                            )}
                            {showUseSigned && (
                                <div>
                                    <a onClick={() => this.props.onActionClick(ExportMode.UseSigned)} className={disableLinkCssClass}>
                                        {cacheStore.GetString("UseSignedCopyLabel")}
                                    </a>
                                </div>
                            )}
                            {isSignOnPrintAccessValid && showRevokeSignature && (
                                <div>
                                    <a onClick={() => this.props.onRevokeSignature()} className={disableLinkCssClass} data-cy="planActionLinks-revokeSignature">
                                        {cacheStore.GetString("RevokeSignatureLabel")}
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className={`${styles.smallActionLink}${this.props.disable ? " " + styles.disabledActionLink : null}`}>
                    <a onClick={() => this.props.onActionClick(ExportMode.Print)} className={disableLinkCssClass} data-cy="planActionLinks-print">
                        {cacheStore.GetString("PrintPlanLabel")}
                    </a>
                </div>
                <div className={`${styles.smallActionLink}${this.props.disable ? " " + styles.disabledActionLink : null}`}>
                    {showFax && (
                        <a onClick={() => this.props.onActionClick(ExportMode.Fax)} className={disableLinkCssClass}>
                            {cacheStore.GetString("FaxVerbLabel")}
                        </a>
                    )}
                </div>
                <div>
                    {showSentFaxes && (
                        <SentFaxInfoBox
                            data={this.props.sentFaxInfo.map(fax => ({
                                sentFaxItemId: fax.SentFaxItemId,
                                sentBy: fax.SenderEmployeeName,
                                sentDate: fax.SentDate,
                                faxStatus: fax.SentStatus,
                                recipient: fax.RecipientName,
                                recipientFax: fax.RecipientFax,
                                receivedDate: fax.ReceiveDate,
                                faxId: fax.Id,
                                itemType: fax.SentFaxItemTypeId,
                                senderEmployeeId: fax.SenderEmployeeId,
                                hasAttachment: fax.HasAttachment
                            }))}
                            parentElementId="medical-note-container"
                            tooltipId={`react-tooltip-${this.props.formId}`}
                        />
                    )}
                </div>
                <div>
                    {showGeneralResendButton && (
                        <div className={styles.smallActionLink}>
                            <ResendFaxButton
                                sentFaxItemId={this.props.sentFaxInfo.firstOrDefault()?.SentFaxItemId}
                                sentFaxIds={[SentFaxStatusHelper.getLastFailedSentFaxId(this.props.sentFaxInfo)]}
                            />
                        </div>
                    )}
                </div>
                <div>
                    {this.props.showPortalStatusButton && this.props.portalStatusButton != null && (
                        <div className={styles.largeActionLink}>
                            {this.props.portalStatusButton}
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
